import { debounce } from '@fe/core';
import { CMP, productAltitudeGrouped, SFC, WeatherSettingsChangeSource } from '@fe/weather';
import React from 'react';
import useWeatherService from '../hooks/use-weather-service';
import useSelectedWeather from './hooks/use-selected-weather';

const MAX_ALTITUDE = 640;
const MIN_ALTITUDE = CMP;
const VALUE_RANGE = MAX_ALTITUDE - MIN_ALTITUDE;
const STEP_LENGTH = 20;

type Stop = { text: string; value: number };
const stops: Stop[] = [];
function createStops() {
    for (let i = MAX_ALTITUDE; i > SFC; i -= STEP_LENGTH) {
        stops.push({ text: i.toString().padStart(3, '0'), value: i });
    }
    stops.push({ text: 'SFC', value: SFC });
    stops.push({ text: 'CMP', value: CMP });
}
createStops();

const WeatherAltitude = () => {
    const weatherService = useWeatherService();
    const selectedWeather = useSelectedWeather({
        ignoreEvents: ['visibleFrameChanged']
    });

    const availableAltitudeProducts = React.useMemo(
        () =>
            selectedWeather
                .filter((x) => x.product.group !== null && x.product.altitude !== null && x.visible && x.gdf)
                .map((x) => productAltitudeGrouped[x.product.group!])
                .filter(Boolean)
                .flat()
                .map((x) => x!.altitude!),
        [selectedWeather]
    );

    const [value, setValue] = React.useState(-MIN_ALTITUDE / VALUE_RANGE);
    const divRef = React.createRef<HTMLDivElement>();

    const isMouseDown = React.useRef(false);
    const isValueSet = React.useRef(false);

    // The reason those 2 method differ is becaus we don't want to be sending settings updates to blazor with every mouse move just send then the mouse button is released
    // This should be removed when we no longer need blazor
    const updateSettingsAltitude = React.useCallback(
        (value: number) =>
            weatherService?.updateSettings(
                (x) => (x.altitude.value = VALUE_RANGE * value + MIN_ALTITUDE),
                WeatherSettingsChangeSource.altitudeValue
            ),
        [weatherService]
    );

    const updateAltitudeValue = React.useMemo(
        () => debounce<[number]>((value) => weatherService?.updateAltitude(VALUE_RANGE * value + MIN_ALTITUDE), 200),
        [weatherService]
    );

    React.useEffect(() => {
        if (!divRef.current) {
            return;
        }
        const div = divRef.current;
        const handlerDown = (e: MouseEvent) => {
            e.preventDefault();
            isMouseDown.current = true;
        };

        const handlerMove = (e: MouseEvent) => {
            if (!isMouseDown.current) {
                return;
            }
            const rect = div.getBoundingClientRect();
            const effectiveHeight = rect.height;
            const rawValue = (rect.bottom - e.clientY) / effectiveHeight;
            const clamped = Math.max(0, Math.min(1, rawValue));
            setValue(clamped);
            updateAltitudeValue(clamped);
            isValueSet.current = true;
        };

        const handlerUp = (e: MouseEvent) => {
            if (isMouseDown.current) {
                const rect = div.getBoundingClientRect();
                const effectiveHeight = rect.height;
                const rawValue = (rect.bottom - e.clientY) / effectiveHeight;
                const clamped = Math.max(0, Math.min(1, rawValue));
                setValue(clamped);
                updateSettingsAltitude(clamped);
                updateAltitudeValue(clamped);
                isValueSet.current = true;
            }
            isMouseDown.current = false;
        };

        document.addEventListener('mousemove', handlerMove);
        document.addEventListener('mouseup', handlerUp);
        divRef.current.addEventListener('mousedown', handlerDown);

        return () => {
            divRef.current?.removeEventListener('mousedown', handlerDown);
            document.removeEventListener('mousemove', handlerMove);
            document.removeEventListener('mouseup', handlerUp);
        };
    }, [divRef.current, updateSettingsAltitude, updateAltitudeValue]);

    React.useEffect(() => {
        if (weatherService) {
            setValue((weatherService.getSettings().altitude.value - MIN_ALTITUDE) / VALUE_RANGE);
        }

        return weatherService?.on('view.changed', () => {
            setValue((weatherService.getSettings().altitude.value - MIN_ALTITUDE) / VALUE_RANGE);
        }).unsubscribe;
    }, [weatherService]);

    return (
        <div className={'h-100' + (availableAltitudeProducts.length === 0 ? ' d-none' : '')}>
            <div
                id="weather-altitude-slider"
                className="h-100 k-card p-2 py-3 map-tools-bg-color"
                style={{
                    cursor: 'pointer',
                    width: 40,
                    padding: '0.45rem',
                    minHeight: '350px',
                    maxHeight: '500px'
                }}>
                <div className="flex-grow-1 position-relative" ref={divRef}>
                    <div
                        style={{
                            left: '0',
                            width: '2px',
                            top: '-5px',
                            bottom: '-5px',
                            position: 'absolute',
                            cursor: 'pointer',
                            backgroundColor: 'var(--border-color)'
                        }}
                    />

                    {stops.map((x) => (
                        <div
                            key={x.value}
                            className="d-flex align-items-center justify-content-end input-transparent"
                            style={{
                                position: 'absolute',
                                left: 0,
                                gap: '.25em',
                                bottom: `${(((x.value - MIN_ALTITUDE) / VALUE_RANGE) * 100).toFixed(2)}%`,
                                transform: 'translate(0, 50%)',
                                fontSize: '0.68em'
                            }}>
                            <div
                                style={{
                                    width: '6px',
                                    height: '2px',
                                    backgroundColor: 'var(--border-color)'
                                }}
                            />
                            <div>{x.text}</div>
                        </div>
                    ))}

                    <div
                        className="slider-drag-handle"
                        style={{
                            backgroundColor: 'rgba(0, 120, 250, 0.40)',
                            height: '8px',
                            width: '13px',
                            border: '1px solid var(--fe-primary)',
                            position: 'absolute',
                            transform: 'translate(-50%, 50%)',
                            bottom: `${(value * 100).toFixed(2)}%`,
                            left: '1px'
                        }}
                    />
                    {availableAltitudeProducts.map((x, i) => (
                        <div
                            key={i}
                            style={{
                                backgroundColor: 'var(--fe-primary)',
                                height: '6px',
                                width: '6px',
                                position: 'absolute',
                                transform: 'rotate(45deg) translate(0, 50%)',
                                bottom: `${((((x ?? 0) - MIN_ALTITUDE) / VALUE_RANGE) * 100).toFixed(2)}%`,
                                left: '0'
                            }}></div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default WeatherAltitude;
